import { useEffect, useState } from "react";
import { Plus, KeyRound, Pencil, Trash2, Copy, Building2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { SectionTitle, ModuleBadge } from "@/components/shared";
import { adminApi } from "@/lib/apiClient";
import { toast } from "sonner";

export default function TenantsTab({ modules }) {
  const [tenants, setTenants] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", licensed_modules: [], allowed_personas: [] });
  const [revealKey, setRevealKey] = useState(null);

  const load = async () => {
    const [t, p] = await Promise.all([adminApi.get("/admin/tenants"), adminApi.get("/admin/personas")]);
    setTenants(t.data.tenants);
    setPersonas(p.data.personas);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", licensed_modules: [], allowed_personas: [] });
    setDrawerOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, licensed_modules: t.licensed_modules || [], allowed_personas: t.allowed_personas || [] });
    setDrawerOpen(true);
  };

  const toggleModule = (key) => {
    setForm((f) => ({
      ...f,
      licensed_modules: f.licensed_modules.includes(key)
        ? f.licensed_modules.filter((m) => m !== key)
        : [...f.licensed_modules, key],
    }));
  };
  const togglePersona = (id) => {
    setForm((f) => ({
      ...f,
      allowed_personas: f.allowed_personas.includes(id)
        ? f.allowed_personas.filter((p) => p !== id)
        : [...f.allowed_personas, id],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Tenant name required"); return; }
    try {
      if (editing) {
        await adminApi.patch(`/admin/tenants/${editing.id}`, form);
        toast.success("Tenant updated");
      } else {
        const r = await adminApi.post("/admin/tenants", form);
        toast.success("Tenant created");
        setRevealKey({ name: r.data.name, api_key: r.data.api_key });
      }
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const reveal = async (t) => {
    try {
      const r = await adminApi.get(`/admin/tenants/${t.id}/key`);
      setRevealKey({ name: t.name, api_key: r.data.api_key });
    } catch (e) { toast.error("Could not reveal key"); }
  };

  const remove = async (t) => {
    try {
      await adminApi.delete(`/admin/tenants/${t.id}`);
      toast.success("Tenant deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied to clipboard"); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Tenants &amp; Module Licensing</SectionTitle>
        <Button onClick={openCreate} data-testid="tenants-create-button"><Plus className="h-4 w-4" /> Create Tenant</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="tenants-list">
        {tenants.map((t) => (
          <Card key={t.id} className="p-5 bg-card border-border" data-testid={`tenant-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-lg bg-secondary grid place-items-center"><Building2 className="h-4 w-4 text-primary" /></div>
                <div>
                  <div className="font-display font-semibold">{t.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{t.api_key_masked}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => reveal(t)} data-testid={`tenant-api-key-reveal-button-${t.id}`} title="Reveal key"><KeyRound className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`tenant-edit-button-${t.id}`} title="Edit"><Pencil className="h-4 w-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid={`tenant-delete-button-${t.id}`} title="Delete"><Trash2 className="h-4 w-4 text-rose-300" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-card border-border">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {t.name}?</AlertDialogTitle>
                      <AlertDialogDescription>This revokes the tenant&apos;s key and access. This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(t)} data-testid={`tenant-delete-confirm-${t.id}`}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-xs text-muted-foreground mb-2">Licensed modules ({(t.licensed_modules || []).length})</div>
              <div className="flex flex-wrap gap-1.5">
                {(t.licensed_modules || []).map((mk) => (
                  <ModuleBadge key={mk} moduleKey={mk} name={modules.find((m) => m.key === mk)?.name || mk} />
                ))}
                {(t.licensed_modules || []).length === 0 && <span className="text-xs text-muted-foreground">None</span>}
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              Personas: {(t.allowed_personas || []).length ? (t.allowed_personas || []).join(", ") : "All"}
            </div>
          </Card>
        ))}
      </div>

      {/* Create/Edit drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-[92vw] sm:w-[480px] bg-card overflow-y-auto scrollbar-thin">
          <SheetHeader>
            <SheetTitle className="font-display">{editing ? "Edit Tenant" : "Create Tenant"}</SheetTitle>
          </SheetHeader>
          <div className="mt-5 space-y-5">
            <div>
              <Label className="text-xs text-muted-foreground">Tenant name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name-input" className="mt-1 bg-secondary/40" placeholder="e.g. GVI Executive Committee" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-2">Module licenses</div>
              <div className="space-y-2">
                {modules.map((m) => (
                  <div key={m.key} className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium flex items-center gap-1.5">
                        {m.name}
                        {m.sensitive && <span className="text-[10px] text-rose-300 border border-rose-500/30 rounded px-1">restricted</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">{m.description}</div>
                    </div>
                    <Switch
                      checked={form.licensed_modules.includes(m.key)}
                      onCheckedChange={() => toggleModule(m.key)}
                      data-testid={`tenant-module-toggle-${m.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-2">Allowed personas (none = all)</div>
              <div className="grid grid-cols-1 gap-2">
                {personas.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2 cursor-pointer">
                    <Checkbox
                      checked={form.allowed_personas.includes(p.id)}
                      onCheckedChange={() => togglePersona(p.id)}
                      data-testid={`tenant-persona-toggle-${p.id}`}
                    />
                    <span className="text-sm">{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button onClick={save} className="w-full" data-testid="tenant-drawer-save-button">{editing ? "Save Changes" : "Create Tenant"}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Reveal key dialog */}
      <Dialog open={!!revealKey} onOpenChange={(o) => !o && setRevealKey(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">API Key — {revealKey?.name}</DialogTitle>
            <DialogDescription>Share this key with the tenant. Treat it like a password.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/40 p-3">
            <code className="flex-1 font-mono text-sm text-primary break-all" data-testid="revealed-api-key">{revealKey?.api_key}</code>
            <Button variant="secondary" size="icon" onClick={() => copy(revealKey?.api_key)} data-testid="copy-api-key-button"><Copy className="h-4 w-4" /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
